import { FC, ReactNode, useMemo, useState } from 'react';

import { Menu, Popover } from '@headlessui/react';
import { VariantProps, cva } from 'class-variance-authority';
import { BsInfoCircle } from 'react-icons/bs';
import { Button, Tooltip } from 'ui/components/common';
import { cn } from 'utils/helpers';
import Avatar, { AvatarGroup } from 'ui/components/img/Avatar';
import { useFloating, offset, flip } from '@floating-ui/react';
import { autoUpdate } from '@floating-ui/react';
import { VscClose } from 'react-icons/vsc';
import { FaPlus } from 'react-icons/fa';
import { SIZE } from 'utils/constants';
import { HiOutlineMagnifyingGlass } from 'react-icons/hi2';
import { debounce, differenceBy } from 'lodash';
import type { Placement } from '@floating-ui/react';

import Input, { inputClasses } from '../Input/Input';
import Checkbox from '../Checkbox';

const avatarSelectControl = cva(
  [
    // 'min-w-[285px]',
    'bg-fill-tertiary',
    'text-left',
    'text-content',
    '!shadow-none rounded-md',
    'text-sm leading-5 tracking-[0.56px]',
    'border border-separator-secondary',
    'disabled:border-fill-secondary disabled:text-disabled',
    'group-focus:border-primary-base group-focus-visible:border-primary-base',
  ],
  {
    variants: {
      size: {
        sm: ['text-sm'],
        md: ['text-sm', 'h-[42px]'],
        lg: ['text-base', 'h-[52px]', 'px-2'],
      },
      isFocused: {
        true: ['!border-primary-base'],
      },
      state: {
        danger: ['!border-danger-base'],
        warning: ['!border-warning-base'],
        success: ['!border-success-base'],
      },
    },
    defaultVariants: {
      size: 'sm',
    },
  },
);

const subtext = cva(['leading-4 text-content mt-2'], {
  variants: {
    state: {
      default: [],
      danger: ['!text-danger-base'],
      warning: ['!text-warning-base'],
      success: ['!text-success-base'],
    },
    size: {
      sm: 'text-xs',
      md: 'text-xs',
      lg: 'text-sm',
    },
  },
});

export interface AvatarOption {
  photoUrl?: string | null;
  itemLabel: string;
  description?: string;
  value: any;
}
type PROPS = VariantProps<typeof avatarSelectControl> & {
  name?: string;
  label?: string;
  labelSubText?: string;
  information?: string;
  required?: boolean;
  subText?: string;
  placeholder?: string | ReactNode;
  onClear?: () => void;
  onClickCreate?: () => void;
  onInputChange: (value: string) => void;
  onClickSelected: (value: string) => void;
  onSelect: (value: string) => void;
  values?: readonly AvatarOption[];
  selected?: AvatarOption[];
  placement?: Placement;
  isSingleSelect?: boolean;
};

const SelectAvatarPopover: FC<PROPS> = ({
  label,
  placement,
  name,
  information,
  required,
  labelSubText,
  state,
  size,
  placeholder,
  onClickCreate,
  onInputChange,
  onClickSelected,
  onSelect,
  onClear,
  values,
  isSingleSelect = false,
  selected = [],
  subText,
}) => {
  const { refs, floatingStyles } = useFloating({
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [flip(), offset({ mainAxis: 2 })],
  });

  const [isOpen, setIsOpen] = useState(false);

  const selectedAvatars = useMemo(() => {
    if (selected.length === 1) {
      return (
        <div className="flex gap-[14px] items-center h-full">
          <Avatar
            size={SIZE.Small}
            user={{
              fullName: selected[0]?.itemLabel,
              photoUrl: selected[0]?.photoUrl,
            }}
          />
          {selected[0]?.itemLabel}
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <AvatarGroup
          size={SIZE.Small}
          users={selected.map(({ itemLabel, photoUrl }) => ({
            fullName: itemLabel,
            photoUrl: photoUrl,
          }))}
        />
        <div>{selected.length} Selected</div>
      </div>
    );
  }, [selected]);

  return (
    <Popover>
      <div className="w-full relative">
        {(label || information) && (
          <div className="flex items-center justify-between mb-[10px]">
            {label ? (
              <label htmlFor={name} className="text-sm font-semibold">
                {label}
                {required ? (
                  <span className="text-danger-base">*</span>
                ) : (
                  <span className="text-xs ml-[3px] leading-[18px] text-content">
                    {labelSubText}
                  </span>
                )}
              </label>
            ) : null}
            {information ? (
              <div className="text-content">
                <Tooltip title={information} placement="top">
                  <BsInfoCircle className="w-4 h-4" />
                </Tooltip>
              </div>
            ) : null}
          </div>
        )}
        <div
          className={cn(
            inputClasses({ size: size as SIZE, textType: state }),
            'flex justify-between items-center',
            'py-0 flex-shrink-0 cursor-pointer',
          )}
          ref={refs.setReference}
        >
          <Popover.Button
            onClick={() => setIsOpen((prev) => !prev)}
            className="flex-1 w-full text-start h-full outline-0"
          >
            <div>
              {selected.length ? (
                selectedAvatars
              ) : (
                <div className="text-content text-sm">{placeholder}</div>
              )}
            </div>
          </Popover.Button>
          {selected.length && onClear ? (
            <VscClose
              role="button"
              onClick={(e) => {
                e.stopPropagation();
                onClear?.();
              }}
              className="w-5 h-5 transition hover:text-primary-base flex-shrink-0"
            />
          ) : null}
        </div>
        {isOpen && (
          <>
            <div
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 w-screen h-screen z-[49]"
            ></div>
            <Popover.Panel
              static
              ref={refs.setFloating}
              style={floatingStyles}
              className={cn(
                'w-full',
                'rounded-md border z-50 border-secondary-lightest',
                'bg-fill-tertiary',
              )}
            >
              <div className="flex items-center justify-between">
                <Button
                  onClick={onClickCreate}
                  label="Add New Record"
                  left
                  icon={FaPlus}
                  intent="link"
                  className={cn(
                    'flex items-center gap-2 py-2 px-3 text-xs',
                    !onClickCreate && 'invisible pointer-events-none',
                  )}
                />

                <Button
                  onClick={onClear}
                  intent="link"
                  label="Clear Selected"
                  className="py-2 px-3 whitespace-nowrap"
                />
              </div>
              <Input
                placeholder="Search..."
                className="mt-2 mx-2"
                size={SIZE.Small}
                icon={
                  <HiOutlineMagnifyingGlass className="h-6 w-6 text-content" />
                }
                onChange={debounce((e) => onInputChange(e.target.value), 500)}
              />
              <div className="my-2"></div>
              {/* Array of Selected Options*/}
              <div className="border-b border-separator-secondary">
                <Menu>
                  <Menu.Items
                    className={'max-h-[120px] overflow-y-auto'}
                    static
                  >
                    {selected?.map((item) => (
                      <Menu.Item key={item.value}>
                        {({ active }) => (
                          <div
                            role="button"
                            onClick={() => onClickSelected(item.value)}
                            className={cn(
                              'flex flex-1 gap-[14px] !items-center h-full',
                              'cursor-pointer',
                              'px-4 py-[10px] hover:bg-fill-primary',
                              active ? 'bg-fill-primary' : '',
                              'hover:bg-fill-primary !text-secondary-dark',
                            )}
                          >
                            <Checkbox
                              defaultChecked
                              className="pointer-events-none"
                            />
                            <Avatar
                              size={SIZE.Small}
                              user={{
                                fullName: item.itemLabel,
                                photoUrl: item.photoUrl,
                              }}
                            />
                            <div className="max-w-[100%] truncate">
                              <p className="text-sm leading-5 truncate text-secondary">
                                {item?.itemLabel}
                              </p>
                              <p className="text-content text-xs leading-4 truncate">
                                {item?.description}
                              </p>
                            </div>
                          </div>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Menu>
              </div>

              {/* Array of Options*/}
              <Menu>
                <Menu.Items className={'max-h-[200px] overflow-y-auto'} static>
                  {differenceBy(values, selected, 'value')?.map((item) => (
                    <Menu.Item key={item.value}>
                      {({ active }) => (
                        <div
                          role="button"
                          onClick={() => {
                            onSelect(item.value);
                            if (isSingleSelect) {
                              setIsOpen(false);
                            }
                          }}
                          className={cn(
                            'cursor-pointer',
                            'flex flex-1 gap-[14px] !items-center h-full',
                            'px-4 py-[10px] hover:bg-fill-primary',
                            active ? 'bg-fill-primary' : '',
                            'hover:bg-fill-primary !text-secondary-dark',
                          )}
                        >
                          <Checkbox className="pointer-events-none" />
                          <Avatar
                            size={SIZE.Small}
                            user={{
                              fullName: item.itemLabel,
                              photoUrl: item.photoUrl,
                            }}
                          />
                          <div className="max-w-[100%] truncate">
                            <p className="text-sm leading-5 truncate text-secondary">
                              {item?.itemLabel}
                            </p>
                            <p className="text-content text-xs leading-4 truncate">
                              {item?.description}
                            </p>
                          </div>
                        </div>
                      )}
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </Menu>
            </Popover.Panel>
          </>
        )}

        {!!subText && (
          <div className={cn(subtext({ state, size }))}>{subText}</div>
        )}
      </div>
    </Popover>
  );
};

export default SelectAvatarPopover;
